import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { key: "overview", label: "Tổng quan", icon: "ri-dashboard-3-line" },
  { key: "jobs", label: "Tin tuyển dụng", icon: "ri-briefcase-line" },
  { key: "candidates", label: "Ứng viên", icon: "ri-user-line" },
  { key: "analytics", label: "Thống kê", icon: "ri-bar-chart-2-line" },
  { key: "profile", label: "Hồ sơ doanh nghiệp", icon: "ri-building-4-line" },
  { key: "account", label: "Quản lý tài khoản", icon: "ri-settings-3-line" },
];

interface SidebarProps {
  activeKey: string;
  onNavigate: (key: string) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({ activeKey, onNavigate, mobileOpen = false, onMobileClose }: SidebarProps) {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileClosing, setMobileClosing] = useState(false);

  const companyName = user?.company_name || "Doanh nghiệp";

  const handleClose = () => {
    setMobileClosing(true);
    setTimeout(() => {
      setMobileClosing(false);
      onMobileClose?.();
    }, 220);
  };

  const handleNav = (key: string) => {
    onNavigate(key);
    handleClose();
  };

  const sidebarNav = (
    <>
      <nav className="py-4 px-3">
        <div className="space-y-1">
          {navItems.map((item) => {
            const isActive = activeKey === item.key;
            return (
              <button
                key={item.key}
                onClick={() => handleNav(item.key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 cursor-pointer whitespace-nowrap group ${
                  isActive
                    ? "bg-primary-100/80 text-primary-700"
                    : "text-foreground-600 hover:bg-background-200/80 hover:text-foreground-900"
                }`}
                title={item.label}
              >
                <div className={`w-5 h-5 flex items-center justify-center shrink-0 ${
                  isActive ? "text-primary-500" : "text-foreground-400 group-hover:text-foreground-600"
                }`}>
                  <i className={`${item.icon} text-base`}></i>
                </div>
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <div className="p-3 border-t border-background-200/70">
        <div className="px-1 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-accent-700">
                {companyName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground-800 truncate">{companyName}</p>
              <p className="text-[10px] text-foreground-500 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
        <Link
          to="/"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-500 hover:bg-background-200/80 hover:text-foreground-700 transition-colors no-underline cursor-pointer"
          title="Về trang chủ"
          onClick={handleClose}
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-home-3-line text-base"></i>
          </div>
          <span className="text-sm">Về trang chủ</span>
        </Link>
      </div>
    </>
  );

  const showMobile = mobileOpen || mobileClosing;

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex h-screen sticky top-0 flex-col bg-background-100 border-r border-background-200/70 transition-all duration-300 shrink-0 ${
          collapsed ? "w-[72px]" : "w-[240px]"
        }`}
      >
        <div className="flex items-center gap-3 px-5 h-16 border-b border-background-200/70 shrink-0">
          <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg shrink-0">
            <i className="ri-building-4-line text-white text-lg"></i>
          </div>
          {!collapsed && (
            <Link to="/doanh-nghiep" className="no-underline">
              <span className="font-heading font-bold text-foreground-950 text-base whitespace-nowrap">
                HR<span className="text-primary-500">Portal</span>
              </span>
            </Link>
          )}
        </div>
        {sidebarNav}
        <div className="p-3 border-t border-background-200/70">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-foreground-500 hover:bg-background-200/80 hover:text-foreground-700 transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className={`text-base ${collapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line"}`}></i>
            </div>
          </button>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {showMobile && (
        <>
          <div
            className={`md:hidden fixed inset-0 z-40 ${mobileClosing ? "animate-fade-out" : "animate-fade-in"}`}
            style={{ background: mobileClosing ? "rgba(0,0,0,0)" : "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}
            onClick={handleClose}
          ></div>
          <aside className={`md:hidden fixed top-0 left-0 z-50 w-64 h-screen bg-background-100 border-r border-background-200/70 flex flex-col shadow-2xl ${mobileClosing ? "animate-slide-out-left" : "animate-slide-in-left"}`}>
            <div className="flex items-center justify-between px-4 h-16 border-b border-background-200/70 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
                  <i className="ri-building-4-line text-white text-base"></i>
                </div>
                <span className="font-heading font-bold text-foreground-950 text-base">HR Portal</span>
              </div>
              <button
                onClick={handleClose}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-200/80 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg text-foreground-600"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {sidebarNav}
            </div>
          </aside>
        </>
      )}
    </>
  );
}